import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { useDeleteParkingGuestReason } from 'src/features/parkingManagement/api/ReasonsForGuest/useDeleteParkingGuestReason';
import {
    GUEST_REASONS_RESOURCE,
    type GuestReason,
    useGetParkingGuestReasons,
} from 'src/features/parkingManagement/api/ReasonsForGuest/useGetParkingGuestReasons';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import EditReasonForGuest from 'src/features/parkingManagement/components/ReasonsForGuest/EditReasonForGuest';
import NewReasonForGuest from 'src/features/parkingManagement/components/ReasonsForGuest/NewReasonForGuest';
import { ReasonsForGuestActions } from 'src/features/parkingManagement/components/ReasonsForGuest/ReasonsForGuestActions';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/hooks/usePagination';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import {
    DataTable,
    DataTableColumnGenericHeaderDefinition,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { useTitleStore } from 'src/stores/useTitleStore';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

const ReasonsForGuest = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { data } = useGetParking({ parkingId: parkingId || '', config: { enabled: !!parkingId } });
    const { setTitle, removeTitle } = useTitleStore();

    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = React.useState<boolean>(false);
    const [selectedReason, setSelectedReason] = React.useState<GuestReason | undefined>(undefined);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const permissionDetailsManage = useHasPermissions([PERMISSION.GUEST_REASONS_MANAGE]);

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        return () => removeTitle();
    }, [data, formatMessage]);

    const {
        data: guestReasonsData,
        isError,
        isLoading,
    } = useGetParkingGuestReasons({
        parkingId: parkingId || '',
        ...queryPaginationOptions,
        config: { enabled: !!parkingId },
    });

    const { mutate: deleteGuestReason } = useDeleteParkingGuestReason({
        onSuccess: () => {
            setIsDeleteModalOpen(false);
            void queryClient.invalidateQueries({
                queryKey: [GUEST_REASONS_RESOURCE],
            });
            toast({
                title: formatMessage(parkingManagementMessages.reasonDeleted),
                description: formatMessage(parkingManagementMessages.reasonDeletedSuccessfully),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const handleActionModal = (type: 'edit' | 'delete', reason?: GuestReason, isOpen: boolean = false) => {
        setSelectedReason(reason);
        if (type === 'edit') {
            setIsEditModalOpen(isOpen);
        } else {
            setIsDeleteModalOpen(isOpen);
        }
    };

    const openDeleteModal = (reason: GuestReason) => handleActionModal('delete', reason, true);
    const openEditModal = (reason: GuestReason) => handleActionModal('edit', reason, true);

    const columnHelper = createColumnHelper<GuestReason>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            cell: (props) => (
                <ReasonsForGuestActions {...props} deleteCallback={openDeleteModal} editCallback={openEditModal} />
            ),
            maxSize: 45,
        }),
    ];

    return (
        <>
            <DataTable
                columns={columns}
                data={guestReasonsData}
                pagination={pagination}
                getRowId={(row) => row.id.toString()}
                semantics={{
                    title: formatMessage(parkingManagementMessages.reasonsForGuest),
                    entryName: formatMessage(parkingManagementMessages.reason),
                    entryNames: formatMessage(parkingManagementMessages.reasons),
                }}
                isError={isError}
                isLoading={isLoading}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                    newEntry: permissionDetailsManage ? NewReasonForGuest : undefined,
                }}
            />
            {isDeleteModalOpen && selectedReason && (
                <ConfirmationModal
                    isModalOpen={isDeleteModalOpen}
                    setIsModalOpen={setIsDeleteModalOpen}
                    dialogDetails={{
                        title: { ...generalMessages.delete },
                        subTitle: { ...parkingManagementMessages.deleteGuestReason },
                        icon: ICON_MAP.DELETE,
                        confirmationMessage: { ...parkingManagementMessages.guestReasonDeleteConfirmation },
                    }}
                    callbackButton={{
                        variant: 'destructiveLight',
                        buttonTitle: generalMessages.delete,
                        callbackFn: () => {
                            return (
                                parkingId && deleteGuestReason({ parkingId: parkingId, id: selectedReason.id }),
                                setIsDeleteModalOpen(false)
                            );
                        },
                    }}
                />
            )}
            {isEditModalOpen && selectedReason && (
                <EditReasonForGuest
                    selectedReason={selectedReason}
                    isModalOpen={isEditModalOpen}
                    setIsModalOpen={setIsEditModalOpen}
                />
            )}
        </>
    );
};

export default ReasonsForGuest;
